import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import {
  Boxes,
  Plus,
  RefreshCw,
  Search,
  Network,
  Lightbulb,
  AlertTriangle,
  ChevronRight,
  Trash2,
  Eye,
  GitBranch,
  Target,
  Link,
  Filter,
  BarChart3,
  BookOpen,
  Zap,
  Info
} from 'lucide-react';

// P0 FIX: Use same-origin API calls to avoid CORS issues in production
// All API calls must use relative URLs (/api/...) - browser uses same origin
const API_URL = '';

export default function RackManager({ examProfileId, getAuthHeaders, isActive = false }) {
  // State
  const [racks, setRacks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [selectedRack, setSelectedRack] = useState(null);
  const [showBuildDialog, setShowBuildDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [conceptFilter, setConceptFilter] = useState('');
  
  // Build form
  const [buildForm, setBuildForm] = useState({
    seed_concept_id: '',
    seed_aku_id: '',
    depth: 2,
    include_similar: true,
    include_contrasts: true,
    include_siblings: true
  });
  const [building, setBuilding] = useState(false);
  
  // Available concepts for dropdown
  const [availableConcepts, setAvailableConcepts] = useState([]);
  
  // Fetch helper
  const fetchWithAuth = useCallback(async (endpoint, options = {}) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }
    
    return response.json();
  }, [getAuthHeaders]);
  
  // Load racks with pagination
  const loadRacks = useCallback(async () => {
    if (!examProfileId) return;
    
    setLoading(true);
    try {
      let endpoint = `/api/racks?exam_profile_id=${examProfileId}&page=1&page_size=50`;
      if (conceptFilter) {
        endpoint += `&concept_id=${conceptFilter}`;
      }
      
      const data = await fetchWithAuth(endpoint);
      // Handle paginated response
      setRacks(data.items || data);
    } catch (error) {
      toast.error(`Fehler beim Laden: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [examProfileId, conceptFilter, fetchWithAuth]);
  
  // Load stats
  const loadStats = useCallback(async () => {
    if (!examProfileId) return;
    
    try {
      const data = await fetchWithAuth(`/api/racks/stats?exam_profile_id=${examProfileId}`);
      setStats(data);
    } catch (error) {
      console.error('Stats load error:', error);
    }
  }, [examProfileId, fetchWithAuth]);
  
  // Load available concepts
  const loadConcepts = useCallback(async () => {
    if (!examProfileId) return;
    
    try {
      const data = await fetchWithAuth(`/api/ontology/nodes?exam_id=${examProfileId}&limit=200`);
      setAvailableConcepts(data.filter(n => n.status !== 'DEPRECATED'));
    } catch (error) {
      console.error('Concepts load error:', error);
    }
  }, [examProfileId, fetchWithAuth]);
  
  // Build rack
  const buildRack = async () => {
    if (!buildForm.seed_concept_id && !buildForm.seed_aku_id) {
      toast.error('Bitte Seed-Konzept oder AKU auswählen');
      return;
    }
    
    setBuilding(true);
    try {
      const result = await fetchWithAuth('/api/racks/build', {
        method: 'POST',
        body: JSON.stringify({
          exam_profile_id: examProfileId,
          ...buildForm
        })
      });
      
      if (result.is_new) {
        toast.success(`Rack erstellt: ${result.rack_id}`);
      } else {
        toast.info('Rack existiert bereits (idempotent)');
      }
      
      setShowBuildDialog(false);
      setBuildForm({
        seed_concept_id: '',
        seed_aku_id: '',
        depth: 2,
        include_similar: true,
        include_contrasts: true,
        include_siblings: true
      });
      
      loadRacks();
      loadStats();
    } catch (error) {
      toast.error(`Fehler: ${error.message}`);
    } finally {
      setBuilding(false);
    }
  };
  
  // Delete rack
  const deleteRack = async (rackId) => {
    if (!window.confirm('Rack wirklich löschen?')) return;
    
    try {
      await fetchWithAuth(`/api/racks/${rackId}`, { method: 'DELETE' });
      toast.success('Rack gelöscht');
      loadRacks();
      loadStats();
    } catch (error) {
      toast.error(`Fehler: ${error.message}`);
    }
  };
  
  // View rack details
  const viewRackDetails = async (rackId) => {
    try {
      const data = await fetchWithAuth(`/api/racks/${rackId}`);
      setSelectedRack(data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error(`Fehler: ${error.message}`);
    }
  };
  
  // Effects - Lazy load only when tab is active
  useEffect(() => {
    // Only load data when tab becomes active AND we haven't loaded yet
    if (isActive && examProfileId && !initialLoadDone) {
      loadRacks();
      loadStats();
      loadConcepts();
      setInitialLoadDone(true);
    }
  }, [isActive, examProfileId, initialLoadDone, loadRacks, loadStats, loadConcepts]);
  
  // Reset initialLoadDone when examProfileId changes
  useEffect(() => {
    setInitialLoadDone(false);
  }, [examProfileId]);
  
  // Filter racks by search
  const filteredRacks = racks.filter(rack => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      rack.rack_id.toLowerCase().includes(query) ||
      rack.focus_concept_names?.some(n => n.toLowerCase().includes(query)) ||
      rack.description?.toLowerCase().includes(query)
    );
  });
  
  // Render difficulty badge
  const renderDifficultyBadge = (difficulty) => {
    const colors = {
      EASY: 'bg-green-100 text-green-700 border-green-200',
      MEDIUM: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      HARD: 'bg-orange-100 text-orange-700 border-orange-200',
      EXPERT: 'bg-red-100 text-red-700 border-red-200'
    };
    return (
      <Badge variant="outline" className={colors[difficulty] || 'bg-gray-100'}>
        {difficulty}
      </Badge>
    );
  };
  
  // Render risk flag badge
  const renderRiskBadge = (flag) => {
    const colors = {
      CONFLICT_SENSITIVE: 'bg-red-50 text-red-600 border-red-200',
      VERSION_SENSITIVE: 'bg-amber-50 text-amber-600 border-amber-200',
      CROSS_FRAMEWORK: 'bg-purple-50 text-purple-600 border-purple-200',
      LOW_EVIDENCE: 'bg-blue-50 text-blue-600 border-blue-200'
    };
    const labels = {
      CONFLICT_SENSITIVE: 'Konflikt',
      VERSION_SENSITIVE: 'Version',
      CROSS_FRAMEWORK: 'Cross-FW',
      LOW_EVIDENCE: 'Low Evidence'
    };
    return (
      <Badge key={flag} variant="outline" className={`text-xs ${colors[flag] || ''}`}>
        {labels[flag] || flag}
      </Badge>
    );
  };
  
  return (
    <div className="space-y-6" data-testid="rack-manager">
      {/* READ-ONLY Info Banner */}
      <Card className="border-indigo-200 bg-indigo-50 dark:bg-indigo-950/20">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Boxes className="w-5 h-5 text-indigo-600 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-indigo-800 dark:text-indigo-200">
                Rack Manager (P1.3)
              </p>
              <p className="text-sm text-indigo-700 dark:text-indigo-300 mt-1">
                Racks sind subgraph-basierte Item-Familien zur systematischen Distractor-Generierung.
                Distractor-Kandidaten stammen aus <strong>CONTRASTS_WITH</strong>, <strong>SIMILAR_TO</strong>, und <strong>IS_A Siblings</strong>.
              </p>
            </div>
            <Button 
              onClick={() => setShowBuildDialog(true)}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="build-rack-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Rack erstellen
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-indigo-50 border-indigo-200">
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-indigo-700">{stats.total_racks || 0}</p>
              <p className="text-sm text-indigo-600">Gesamt Racks</p>
            </CardContent>
          </Card>
          <Card className="bg-violet-50 border-violet-200">
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-violet-700">{stats.avg_focus_concepts?.toFixed(1) || 0}</p>
              <p className="text-sm text-violet-600">Ø Focus Concepts</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-purple-700">{stats.avg_distractors?.toFixed(1) || 0}</p>
              <p className="text-sm text-purple-600">Ø Distractors</p>
            </CardContent>
          </Card>
          <Card className="bg-fuchsia-50 border-fuchsia-200">
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-fuchsia-700">{stats.avg_source_akus?.toFixed(1) || 0}</p>
              <p className="text-sm text-fuchsia-600">Ø Source AKUs</p>
            </CardContent>
          </Card>
          <Card className="bg-pink-50 border-pink-200">
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-pink-700">
                {Object.keys(stats.by_ontology_version || {}).length}
              </p>
              <p className="text-sm text-pink-600">Ontology Versions</p>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Search and Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Boxes className="w-5 h-5 text-indigo-600" />
              Racks für {examProfileId}
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => { loadRacks(); loadStats(); }}
              disabled={loading}
              data-testid="refresh-racks-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Suche nach Rack-ID oder Konzept..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="rack-search-input"
              />
            </div>
            <Select value={conceptFilter || 'all'} onValueChange={(v) => setConceptFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-64" data-testid="concept-filter-select">
                <SelectValue placeholder="Alle Konzepte" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Konzepte</SelectItem>
                {availableConcepts.slice(0, 50).map(c => (
                  <SelectItem key={c.node_id} value={c.node_id}>
                    {c.canonical_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Racks Table */}
          <ScrollArea className="h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Focus Concepts</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Distractors</TableHead>
                  <TableHead>AKUs</TableHead>
                  <TableHead>Difficulty</TableHead>
                  <TableHead>Risk Flags</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRacks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      {loading ? 'Lädt...' : 'Keine Racks gefunden'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRacks.map(rack => (
                    <TableRow key={rack.rack_id} className="hover:bg-gray-50">
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <span className="font-medium">
                            {rack.focus_concept_names?.join(', ') || rack.focus_concepts?.join(', ')}
                          </span>
                          <span className="text-xs text-gray-500 font-mono">
                            {rack.rack_id.substring(0, 30)}...
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">
                          {rack.ontology_version_number}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Target className="w-4 h-4 text-orange-500" />
                          <span>{rack.distractor_count || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <BookOpen className="w-4 h-4 text-blue-500" />
                          <span>{rack.source_aku_count || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {renderDifficultyBadge(rack.constraints?.difficulty_band)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {rack.constraints?.risk_flags?.map(flag => renderRiskBadge(flag))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => viewRackDetails(rack.rack_id)}
                            data-testid={`view-rack-${rack.rack_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => deleteRack(rack.rack_id)}
                            data-testid={`delete-rack-${rack.rack_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
      
      {/* Build Rack Dialog */}
      <Dialog open={showBuildDialog} onOpenChange={setShowBuildDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Boxes className="w-5 h-5 text-indigo-600" />
              Neuen Rack erstellen
            </DialogTitle>
            <DialogDescription>
              Rack wird idempotent erstellt. Gleiche Inputs → gleiche Rack-ID.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Seed Concept Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Seed-Konzept</label>
              <Select 
                value={buildForm.seed_concept_id} 
                onValueChange={(v) => setBuildForm(prev => ({ ...prev, seed_concept_id: v, seed_aku_id: '' }))}
              >
                <SelectTrigger data-testid="seed-concept-select">
                  <SelectValue placeholder="Konzept auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {availableConcepts.map(c => (
                    <SelectItem key={c.node_id} value={c.node_id}>
                      {c.canonical_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Depth */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Tiefe (BFS)</label>
              <Select 
                value={String(buildForm.depth)} 
                onValueChange={(v) => setBuildForm(prev => ({ ...prev, depth: parseInt(v) }))}
              >
                <SelectTrigger data-testid="depth-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 (Nur direkte Nachbarn)</SelectItem>
                  <SelectItem value="2">2 (Standard)</SelectItem>
                  <SelectItem value="3">3 (Erweitert)</SelectItem>
                  <SelectItem value="4">4 (Umfangreich)</SelectItem>
                  <SelectItem value="5">5 (Maximum)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Distractor Options */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Distractor-Typen</label>
              <div className="flex flex-wrap gap-2">
                <Badge 
                  variant={buildForm.include_contrasts ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setBuildForm(prev => ({ ...prev, include_contrasts: !prev.include_contrasts }))}
                >
                  CONTRASTS_WITH
                </Badge>
                <Badge 
                  variant={buildForm.include_similar ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setBuildForm(prev => ({ ...prev, include_similar: !prev.include_similar }))}
                >
                  SIMILAR_TO
                </Badge>
                <Badge 
                  variant={buildForm.include_siblings ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setBuildForm(prev => ({ ...prev, include_siblings: !prev.include_siblings }))}
                >
                  IS_A Siblings
                </Badge>
              </div>
            </div>
            
            {/* Info */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex gap-2">
                <Info className="w-4 h-4 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Guardrails (automatisch):</p>
                  <ul className="mt-1 text-xs space-y-1">
                    <li>• Pinned an aktive Ontology-Version</li>
                    <li>• Keine AMBIGUOUS/CONFLICTING AKUs</li>
                    <li>• Scope-aware (nur {examProfileId})</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBuildDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={buildRack}
              disabled={building || (!buildForm.seed_concept_id && !buildForm.seed_aku_id)}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="confirm-build-rack-btn"
            >
              {building ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Erstelle...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Rack erstellen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Rack Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Network className="w-5 h-5 text-indigo-600" />
              Rack Details
            </DialogTitle>
            <DialogDescription className="font-mono text-xs">
              {selectedRack?.rack_id}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRack && (
            <Tabs defaultValue="overview" className="mt-4">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="overview">Übersicht</TabsTrigger>
                <TabsTrigger value="concepts">Konzepte</TabsTrigger>
                <TabsTrigger value="distractors">Distractors</TabsTrigger>
                <TabsTrigger value="akus">AKUs</TabsTrigger>
              </TabsList>
              
              <ScrollArea className="h-[400px] mt-4">
                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Exam Profile</p>
                      <p className="font-medium">{selectedRack.exam_profile_id}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Ontology Version</p>
                      <p className="font-medium font-mono">{selectedRack.ontology_version_number}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Focus Concepts</p>
                      <p className="font-medium">{selectedRack.focus_concepts?.length || 0}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Included Concepts</p>
                      <p className="font-medium">{selectedRack.included_concepts?.length || 0}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Distractor Candidates</p>
                      <p className="font-medium">{selectedRack.distractor_count || 0}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Source AKUs</p>
                      <p className="font-medium">{selectedRack.source_aku_count || 0}</p>
                    </div>
                  </div>
                  
                  {/* Constraints */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Constraints</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {renderDifficultyBadge(selectedRack.constraints?.difficulty_band)}
                        {selectedRack.constraints?.allowed_question_types?.map(t => (
                          <Badge key={t} variant="outline">{t}</Badge>
                        ))}
                        {selectedRack.constraints?.risk_flags?.map(f => renderRiskBadge(f))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Build Params */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Build Parameters</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                        {JSON.stringify(selectedRack.build_params, null, 2)}
                      </pre>
                    </CardContent>
                  </Card>
                </TabsContent>
                
                {/* Concepts Tab */}
                <TabsContent value="concepts" className="space-y-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Target className="w-4 h-4 text-indigo-600" />
                        Focus Concepts ({selectedRack.focus_concepts?.length || 0})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {selectedRack.focus_concept_names?.map((name, i) => (
                          <Badge key={i} className="bg-indigo-100 text-indigo-700 border-indigo-200">
                            {name}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <GitBranch className="w-4 h-4 text-violet-600" />
                        Included Concepts ({selectedRack.included_concepts?.length || 0})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {selectedRack.included_concept_names?.map((name, i) => (
                          <Badge key={i} variant="outline">
                            {name}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  {selectedRack.included_edges?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Link className="w-4 h-4 text-purple-600" />
                          Edges ({selectedRack.included_edges?.length || 0})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-1 text-xs">
                          {selectedRack.included_edges?.slice(0, 20).map((edge, i) => (
                            <div key={i} className="flex items-center gap-2 p-1 bg-gray-50 rounded">
                              <span className="font-mono">{edge.source_id?.substring(0, 10)}</span>
                              <ChevronRight className="w-3 h-3" />
                              <Badge variant="outline" className="text-xs">{edge.relation_type}</Badge>
                              <ChevronRight className="w-3 h-3" />
                              <span className="font-mono">{edge.target_id?.substring(0, 10)}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
                
                {/* Distractors Tab */}
                <TabsContent value="distractors" className="space-y-4">
                  {selectedRack.distractor_candidates?.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                      <p>Keine Distractor-Kandidaten gefunden</p>
                      <p className="text-xs mt-1">Ontology enthält möglicherweise keine CONTRASTS_WITH, SIMILAR_TO oder IS_A Edges</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Konzept</TableHead>
                          <TableHead>Relation</TableHead>
                          <TableHead>Source</TableHead>
                          <TableHead>Rationale</TableHead>
                          <TableHead>Confidence</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedRack.distractor_candidates?.map((d, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium">{d.concept_name}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {d.relation_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs font-mono">
                              {d.source_concept_id?.substring(0, 12)}...
                            </TableCell>
                            <TableCell className="text-sm text-gray-600 max-w-xs truncate">
                              {d.rationale}
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant="outline" 
                                className={d.confidence >= 0.9 ? 'text-green-600' : d.confidence >= 0.7 ? 'text-yellow-600' : 'text-gray-600'}
                              >
                                {(d.confidence * 100).toFixed(0)}%
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </TabsContent>
                
                {/* AKUs Tab */}
                <TabsContent value="akus" className="space-y-4">
                  {selectedRack.source_akus?.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <BookOpen className="w-8 h-8 mx-auto mb-2 text-blue-400" />
                      <p>Keine verknüpften AKUs</p>
                      <p className="text-xs mt-1">
                        Konzepte haben keine AKU v2 Einträge mit passender ontology_version
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">
                        {selectedRack.source_aku_count} AKUs verknüpft (nur SUPPORTED_* und TRAINING_ONLY)
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {selectedRack.source_akus?.slice(0, 50).map(akuId => (
                          <Badge key={akuId} variant="outline" className="font-mono text-xs">
                            {akuId}
                          </Badge>
                        ))}
                        {selectedRack.source_akus?.length > 50 && (
                          <Badge variant="secondary">
                            +{selectedRack.source_akus.length - 50} mehr
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                </TabsContent>
              </ScrollArea>
            </Tabs>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
